import React, {useCallback, useEffect, useMemo, useRef} from "react";
import PropTypes from "prop-types";
import {useTheme} from "../../theme/ThemeProvider";
import BaseChart from "./BaseChart";

const SegmentedBarChart = ({data = [], settings = {}, ...rest}) => {
  const chartSettings = useMemo(() => ({...settings}), [settings]);

  const SegmentedBarChartRenderer = ({
    canvasRef,
    containerRef,
    chartSettings,
    hovering,
    setTooltipData,
    setTooltipPosition,
  }) => {
    const {theme} = useTheme();
    const segmentRefs = useRef([]);

    const drawChart = useCallback(() => {
      if (!canvasRef.current || !containerRef.current || data.length === 0)
        return;

      const containerWidth = containerRef.current.clientWidth || 0;
      const containerHeight = chartSettings.height || 40;
      const borderRadius = chartSettings.borderRadius || theme.radius.md || 4;
      const separation = chartSettings.separation || 0;

      if (containerWidth <= 0 || containerHeight <= 0) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d", {alpha: true});

      const dpr = window.devicePixelRatio || 1;
      canvas.width = containerWidth * dpr;
      canvas.height = containerHeight * dpr;

      ctx.scale(dpr, dpr);
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;

      ctx.clearRect(0, 0, containerWidth, containerHeight);

      const total = data.reduce((sum, item) => sum + item.value, 0);

      const totalSeparationSpace =
        separation > 0 ? (data.length - 1) * separation : 0;
      const availableWidth = containerWidth - totalSeparationSpace;

      const minBarWidth = chartSettings.minBarWidth || 2;

      let currentX = 0;
      segmentRefs.current = [];

      const segmentWidths = data.map((item) => {
        const naturalWidth = (item.value / total) * availableWidth;
        return Math.max(naturalWidth, minBarWidth);
      });

      const totalCalculatedWidth = segmentWidths.reduce(
        (sum, width) => sum + width,
        0,
      );
      const scaleFactor =
        totalCalculatedWidth > availableWidth
          ? availableWidth / totalCalculatedWidth
          : 1;

      data.forEach((item, index) => {
        const segmentWidth = segmentWidths[index] * scaleFactor;

        ctx.fillStyle = item.color;
        ctx.beginPath();

        if (separation > 0) {
          ctx.moveTo(currentX + borderRadius, 0);
          ctx.lineTo(currentX + segmentWidth - borderRadius, 0);
          ctx.quadraticCurveTo(
            currentX + segmentWidth,
            0,
            currentX + segmentWidth,
            borderRadius,
          );
          ctx.lineTo(currentX + segmentWidth, containerHeight - borderRadius);
          ctx.quadraticCurveTo(
            currentX + segmentWidth,
            containerHeight,
            currentX + segmentWidth - borderRadius,
            containerHeight,
          );
          ctx.lineTo(currentX + borderRadius, containerHeight);
          ctx.quadraticCurveTo(
            currentX,
            containerHeight,
            currentX,
            containerHeight - borderRadius,
          );
          ctx.lineTo(currentX, borderRadius);
          ctx.quadraticCurveTo(currentX, 0, currentX + borderRadius, 0);
        } else {
          if (data.length === 1) {
            ctx.moveTo(currentX + borderRadius, 0);
            ctx.lineTo(currentX + segmentWidth - borderRadius, 0);
            ctx.quadraticCurveTo(
              currentX + segmentWidth,
              0,
              currentX + segmentWidth,
              borderRadius,
            );
            ctx.lineTo(currentX + segmentWidth, containerHeight - borderRadius);
            ctx.quadraticCurveTo(
              currentX + segmentWidth,
              containerHeight,
              currentX + segmentWidth - borderRadius,
              containerHeight,
            );
            ctx.lineTo(currentX + borderRadius, containerHeight);
            ctx.quadraticCurveTo(
              currentX,
              containerHeight,
              currentX,
              containerHeight - borderRadius,
            );
            ctx.lineTo(currentX, borderRadius);
            ctx.quadraticCurveTo(currentX, 0, currentX + borderRadius, 0);
          } else if (index === 0) {
            ctx.moveTo(currentX + borderRadius, 0);
            ctx.lineTo(currentX + segmentWidth, 0);
            ctx.lineTo(currentX + segmentWidth, containerHeight);
            ctx.lineTo(currentX + borderRadius, containerHeight);
            ctx.quadraticCurveTo(
              currentX,
              containerHeight,
              currentX,
              containerHeight - borderRadius,
            );
            ctx.lineTo(currentX, borderRadius);
            ctx.quadraticCurveTo(currentX, 0, currentX + borderRadius, 0);
          } else if (index === data.length - 1) {
            ctx.moveTo(currentX, 0);
            ctx.lineTo(currentX + segmentWidth - borderRadius, 0);
            ctx.quadraticCurveTo(
              currentX + segmentWidth,
              0,
              currentX + segmentWidth,
              borderRadius,
            );
            ctx.lineTo(currentX + segmentWidth, containerHeight - borderRadius);
            ctx.quadraticCurveTo(
              currentX + segmentWidth,
              containerHeight,
              currentX + segmentWidth - borderRadius,
              containerHeight,
            );
            ctx.lineTo(currentX, containerHeight);
            ctx.lineTo(currentX, 0);
          } else {
            ctx.rect(currentX, 0, segmentWidth, containerHeight);
          }
        }

        ctx.fill();

        segmentRefs.current.push({
          x: currentX,
          width: segmentWidth,
          dataPoint: item,
          value: (item.value / total) * 100,
        });

        currentX += segmentWidth + separation;
      });
    }, [canvasRef, containerRef, data, chartSettings, theme]);

    const handleMouseMove = useCallback(
      (event) => {
        if (!hovering || !canvasRef.current || !containerRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;

        if (segmentRefs.current.length === 0) return;

        const segment = segmentRefs.current.find(
          (segment) =>
            mouseX >= segment.x && mouseX <= segment.x + segment.width,
        );

        if (segment) {
          const segmentCenterX = segment.x + segment.width / 2;

          setTooltipData({
            items: [
              {
                label:
                  segment.dataPoint.display ||
                  segment.dataPoint.id ||
                  "Unknown",
                value: `${segment.value.toFixed(1)}% - ${segment.dataPoint.count || segment.dataPoint.value || 0} ${segment.dataPoint.count ? "messages" : "items"}`,
                color: segment.dataPoint.color,
              },
            ],
          });

          setTooltipPosition({
            x: segmentCenterX,
            y: 0,
          });
        } else {
          setTooltipData(null);
        }
      },
      [
        canvasRef,
        containerRef,
        hovering,
        setTooltipData,
        setTooltipPosition,
        data,
      ],
    );

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const boundMouseMove = (e) => handleMouseMove(e);
      canvas.addEventListener("mousemove", boundMouseMove);

      return () => {
        canvas.removeEventListener("mousemove", boundMouseMove);
      };
    }, [handleMouseMove]);

    useEffect(() => {
      if (canvasRef.current && containerRef.current && data.length > 0) {
        drawChart();
      }
    }, [drawChart, data]);

    useEffect(() => {
      const handleResize = () => {
        if (canvasRef.current && containerRef.current && data.length > 0) {
          drawChart();
        }
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [drawChart, data.length]);

    return null;
  };

  return (
    <BaseChart
      data={data}
      settings={{
        height: 40,
        padding: {top: 0, right: 0, bottom: 0, left: 0},
        ...chartSettings,
      }}
      renderer={SegmentedBarChartRenderer}
      {...rest}
    />
  );
};

SegmentedBarChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      display: PropTypes.string,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  settings: PropTypes.shape({
    height: PropTypes.number,
    borderRadius: PropTypes.number,
    separation: PropTypes.number,
    minBarWidth: PropTypes.number,
    padding: PropTypes.object,
    tooltip: PropTypes.object,
  }),
};

export default SegmentedBarChart;
